import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {Card, Text, SegmentedButtons, ActivityIndicator} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import DatePickerInput from '../../components/common/DatePickerInput';
import {useReportStore} from '../../store/useReportStore';
import {colors, spacing} from '../../theme';

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDateRange = (period, refDate) => {
  const date = new Date(refDate);
  switch (period) {
    case 'daily':
      return {from: formatDate(date), to: formatDate(date)};
    case 'weekly': {
      const day = date.getDay();
      const start = new Date(date);
      start.setDate(date.getDate() - day);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return {from: formatDate(start), to: formatDate(end)};
    }
    case 'monthly': {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      return {from: formatDate(start), to: formatDate(end)};
    }
    case 'yearly': {
      const start = new Date(date.getFullYear(), 0, 1);
      const end = new Date(date.getFullYear(), 11, 31);
      return {from: formatDate(start), to: formatDate(end)};
    }
    default:
      return {from: formatDate(date), to: formatDate(date)};
  }
};

const PERIODS = [
  {value: 'daily', label: 'Daily'},
  {value: 'weekly', label: 'Weekly'},
  {value: 'monthly', label: 'Monthly'},
  {value: 'yearly', label: 'Yearly'},
];

const ReportDashboardTab = ({orgUnitId}) => {
  const {t} = useTranslation();
  const {dashboard, isLoading, fetchDashboard} = useReportStore();
  const [period, setPeriod] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));

  const loadData = useCallback(() => {
    const {from, to} = getDateRange(period, selectedDate);
    fetchDashboard(from, to, orgUnitId);
  }, [period, selectedDate, orgUnitId, fetchDashboard]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  const formatCurrency = (val) => {
    if (val == null) return '0';
    return Number(val).toLocaleString('en-IN');
  };

  const metrics = dashboard
    ? [
        {label: t('report.totalSales'), value: `₹${formatCurrency(dashboard.totalSales)}`, icon: 'cash-multiple', color: colors.success},
        {label: t('report.totalPayments'), value: `₹${formatCurrency(dashboard.totalPayments)}`, icon: 'cash-check', color: colors.info},
        {label: t('report.ordersReceived'), value: String(dashboard.ordersReceived), icon: 'package-variant', color: colors.primary},
        {label: t('report.ordersDelivered'), value: String(dashboard.ordersDelivered), icon: 'package-variant-closed-check', color: colors.success},
        {label: t('report.ordersPastDue'), value: String(dashboard.ordersPastDue), icon: 'clock-alert-outline', color: colors.error},
        {label: t('report.ordersUpcoming'), value: String(dashboard.ordersUpcoming), icon: 'calendar-clock', color: colors.warning},
      ]
    : [];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <SegmentedButtons
        value={period}
        onValueChange={handlePeriodChange}
        buttons={PERIODS.map(p => ({...p, label: t(`report.${p.value}`)}))}
        style={styles.segmented}
      />

      <DatePickerInput
        label={t('report.selectDate')}
        value={selectedDate}
        onChangeText={setSelectedDate}
        style={styles.datePicker}
      />

      {isLoading ? (
        <ActivityIndicator style={styles.loader} size="large" color={colors.primary} />
      ) : dashboard ? (
        <View style={styles.grid}>
          {metrics.map((item, index) => (
            <Card key={index} style={styles.card} mode="elevated">
              <Card.Content style={styles.cardContent}>
                <Text style={[styles.cardValue, {color: item.color}]}>{item.value}</Text>
                <Text style={styles.cardLabel}>{item.label}</Text>
              </Card.Content>
            </Card>
          ))}
        </View>
      ) : (
        <Text style={styles.noData}>{t('report.noReportData')}</Text>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  contentContainer: {padding: spacing.md},
  segmented: {marginBottom: spacing.md},
  datePicker: {marginBottom: spacing.md},
  loader: {marginTop: spacing.xl},
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  card: {
    width: '48%',
    backgroundColor: colors.surface,
  },
  cardContent: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  cardValue: {
    fontSize: 22,
    fontWeight: 'bold',
  },
  cardLabel: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  noData: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xl,
  },
});

export default ReportDashboardTab;
